import './marketing.css';

const FEATURE_CARDS = [
  {
    title: 'Notice the drag',
    body: 'Catch the moment when a normal task starts feeling heavier than it should.'
  },
  {
    title: 'Try one small rescue',
    body: 'Start with one short support: reset, breathing, writing, or movement.'
  },
  {
    title: 'See patterns over time',
    body: 'Notice what tends to snag you, and what tends to help.'
  }
];

const GUIDE_CARDS = [
  {
    title: 'When everything feels heavier than it should',
    body: 'A short explanation of what “friction” means here, and how to use the app without turning it into more work.'
  },
  {
    title: 'Small rescues for stuck moments',
    body: 'A simple guide to the rescue tools: breathing, brain dump, make it smaller, reset, and movement support.'
  },
  {
    title: 'What the patterns page is actually for',
    body: 'How to notice trends without overthinking them.'
  }
];

export function LandingPage() {
  return (
    <div className="site-shell">
      <header className="site-hero">
        <div className="site-nav">
          <div className="site-brand">
            <span className="site-brand-mark">Rethread</span>
            <span className="site-brand-subtitle">When you lose the thread, start here.</span>
          </div>
          <div className="site-nav-actions">
            <a className="site-link" href="/privacy/">
              Privacy
            </a>
            <a className="site-link" href="/terms/">
              Terms
            </a>
            <a className="site-link site-link-cta" href="/app/">
              Open the app
            </a>
          </div>
        </div>

        <div className="hero-grid">
          <section className="hero-copy">
            <p className="hero-kicker">When you lose the thread, start here.</p>
            <h1>Pick the thread back up.</h1>
            <p className="hero-body">
              Rethread is for the moments when simple things suddenly feel harder to do. Start one small support, then
              try again with a little less pressure.
            </p>
            <div className="hero-actions">
              <a className="hero-button hero-button-primary" href="/app/">
                Open the web app
              </a>
              <a className="hero-button" href="#how-it-works">
                How it works
              </a>
            </div>
            <div className="hero-meta">
              <span>Private by default</span>
              <span>Installable on the web</span>
              <span>Built for low-attention moments</span>
            </div>
          </section>

          <aside className="hero-panel">
            <div className="hero-preview">
              <div className="hero-app-frame">
                <div className="hero-app-bar">
                  <div>
                    <div className="hero-app-title">Rethread</div>
                    <div className="hero-app-subtitle">Quick support</div>
                  </div>
                  <span className="hero-app-pill">Web app</span>
                </div>

                <div className="hero-preview-card hero-preview-card-primary">
                  <div className="hero-preview-kicker">Start here</div>
                  <h2>2-min reset</h2>
                  <p>Stand up, unclench, look away from the screen, and take one slow sip of water.</p>
                  <div className="hero-preview-actions">
                    <span className="hero-preview-chip">2 min</span>
                    <a className="hero-preview-button" href="/app/?startRescue=reset_2m&landingFlow=1">
                      Start
                    </a>
                  </div>
                  <div className="hero-preview-note">After the timer, the app will show you around.</div>
                </div>

                <div className="hero-preview-snapshot">
                  <div className="hero-snapshot-heading">
                    <span className="hero-snapshot-kicker">Pattern snapshot</span>
                    <span className="hero-snapshot-period">Last 7 days</span>
                  </div>
                  <div className="hero-snapshot-list">
                    <div className="hero-snapshot-row">
                      <span>Can’t start</span>
                      <strong>10</strong>
                    </div>
                    <div className="hero-snapshot-row">
                      <span>Too tired</span>
                      <strong>7</strong>
                    </div>
                    <div className="hero-snapshot-row">
                      <span>Avoiding</span>
                      <strong>6</strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </header>

      <main className="site-main">
        <section className="site-section" id="how-it-works">
          <div className="section-heading">
            <p className="section-kicker">How it works</p>
            <h2>Three simple parts.</h2>
          </div>
          <div className="feature-grid">
            {FEATURE_CARDS.map((card) => (
              <article className="feature-card" key={card.title}>
                <h3>{card.title}</h3>
                <p>{card.body}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="site-section site-section-split">
          <div className="section-heading section-heading-emphasis">
            <p className="section-kicker">Why this exists</p>
            <h2>Some days, ordinary tasks cost too much.</h2>
            <p>
              Rethread is built for that point where you are not in crisis, but things still feel strangely hard to do.
            </p>
            <div className="support-principles">
              <article className="support-principle">
                <h3>Notice earlier</h3>
                <p>Catch the snag sooner, before the whole day gets away from you.</p>
              </article>
              <article className="support-principle">
                <h3>Try something small</h3>
                <p>Use a short support that helps you settle and get back in.</p>
              </article>
            </div>
          </div>
          <div className="support-panel">
            <h3>What it is good at</h3>
            <p>It helps you notice when the day is slipping, try one short support, and see what tends to help.</p>
            <div className="support-points">
              <span>Fast logging</span>
              <span>Short rescues</span>
              <span>Useful patterns</span>
            </div>
          </div>
        </section>

        <section className="site-section">
          <div className="section-heading">
            <p className="section-kicker">Resources</p>
            <h2>Short guides that explain the idea behind the app.</h2>
          </div>
          <div className="guide-grid">
            {GUIDE_CARDS.map((card) => (
              <article className="guide-card" key={card.title}>
                <h3>{card.title}</h3>
                <p>{card.body}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="site-section site-cta">
          <div>
            <p className="section-kicker">Start now</p>
            <h2>Open the app and try one small support.</h2>
          </div>
          <div className="hero-actions">
            <a className="hero-button hero-button-primary" href="/app/">
              Open Rethread
            </a>
            <a className="hero-button" href="/privacy/">
              Read the privacy policy
            </a>
          </div>
        </section>
      </main>
    </div>
  );
}

export default LandingPage;
